/**
 * CMS Editor — visual editor for the tenant's landing document.
 *
 * This container owns:
 *   • data flow (useQuery / useMutation for the shared CMS document)
 *   • dirty-state tracking, discard, save & reset flows
 *   • tab + section navigation, sidebar search, per-section collapse
 *
 * All per-section UI lives under ./cms/ so this file stays focused:
 *   • ./cms/SectionBody.jsx      — big switch that renders each section
 *   • ./cms/SEOFields.jsx        — SEO & meta tags form
 *   • ./cms/LiveWidgetSection.jsx — hero live-snapshot widget (own save)
 *   • ./cms/DangerZoneCard.jsx   — destructive sample-data wipe
 *   • ./cms/primitives.jsx       — shared UI helpers (Field, ColorField, …)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Save, Eye, RotateCcw, Users, LayoutDashboard, Palette, Building2, Home,
  FileText, ShieldCheck, Search, Undo2, ImageIcon, Truck, ContactRound,
  Layers, MapPin, MessageSquare, Star, Rocket, Landmark, Info, Package,
  TrendingUp, HelpCircle, Check, Circle, ChevronDown, ChevronsDownUp,
  ChevronsUpDown, Share2, Sparkles, Link2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SectionBody } from "./cms/SectionBody";

const SECTIONS = [
  // BRAND
  { id: "brand-identity",   tab: "brand",   icon: Building2,    label: "Brand identity",         description: "Company name, tagline & master logo" },
  { id: "brand-customer",   tab: "brand",   icon: Users,        label: "Customer portal",        description: "Panel name & optional distinct logo" },
  { id: "brand-admin",      tab: "brand",   icon: LayoutDashboard, label: "Admin console",       description: "Staff ERP navbar branding" },
  { id: "brand-driver",     tab: "brand",   icon: Truck,        label: "Driver app",             description: "Mobile app navbar branding" },
  { id: "brand-taglines",   tab: "brand",   icon: FileText,     label: "Login sub-lines",        description: "Sub-line under the logo on sign-in pages" },

  // THEME
  { id: "theme-public",     tab: "theme",   icon: Palette,      label: "Public site theme",      description: "Colours for the landing page" },
  { id: "theme-customer",   tab: "theme",   icon: Palette,      label: "Customer theme",         description: "Sign-in & portal colours" },
  { id: "theme-staff",      tab: "theme",   icon: Palette,      label: "Staff theme",            description: "Console dark theme" },

  // CONTENT
  { id: "hero",             tab: "content", icon: Rocket,       label: "Hero",                   description: "Headline, sub-line & CTAs" },
  { id: "hero-background",  tab: "content", icon: ImageIcon,    label: "Hero background",        description: "Image · slideshow · video · animation" },
  { id: "about",            tab: "content", icon: Info,         label: "About · Mission · Vision", description: "Body, brand statement, mission, vision & core values" },
  { id: "services",         tab: "content", icon: Package,      label: "Services",               description: "Cards of what you offer" },
  { id: "industries",       tab: "content", icon: Landmark,     label: "Industries served",      description: "Comma-separated list" },
  { id: "commitment",       tab: "content", icon: Sparkles,     label: "Our commitment",         description: "Value promises to customers" },
  { id: "safety",           tab: "content", icon: ShieldCheck,  label: "Safety & Quality",       description: "Procedures & certifications" },
  { id: "benefits",         tab: "content", icon: Check,        label: "Benefits",               description: "Bulleted advantages" },
  { id: "metrics",          tab: "content", icon: TrendingUp,   label: "Trust metrics",          description: "24×7, 50K+ etc." },
  { id: "live-stats",       tab: "content", icon: TrendingUp,   label: "Live snapshot widget",   description: "Hero live counters — auto or manual values" },
  { id: "features",         tab: "content", icon: Sparkles,     label: "Features",               description: "Product feature cards" },
  { id: "how",              tab: "content", icon: Layers,       label: "How it works",           description: "Step-by-step process" },
  { id: "coverage",         tab: "content", icon: MapPin,       label: "Coverage",               description: "Cities & delivery region" },
  { id: "testimonials",     tab: "content", icon: MessageSquare,label: "Testimonials",           description: "Customer quotes" },
  { id: "faq",              tab: "content", icon: HelpCircle,   label: "FAQ",                    description: "Common questions" },
  { id: "app-download",     tab: "content", icon: Star,         label: "App download banner",    description: "iOS / Android CTA card" },
  { id: "contact",          tab: "content", icon: ContactRound, label: "Contact",                description: "Email, phone, WhatsApp, address" },
  { id: "support_contact",  tab: "content", icon: ContactRound, label: "Support desk contact",   description: "In-app support hotline (separate from public contact)" },
  { id: "social",           tab: "content", icon: Share2,       label: "Social media",           description: "Footer icons — enable per platform + set URLs" },
  { id: "footer",           tab: "content", icon: Home,         label: "Footer",                 description: "Bottom-strip tagline & copyright" },

  // SEO
  { id: "seo",              tab: "seo",     icon: Search,       label: "SEO & Meta tags",        description: "Title, description, OpenGraph, structured data" },

  // LEGAL & ACCESS
  { id: "legal",            tab: "legal",   icon: FileText,     label: "Privacy & Terms",        description: "Markdown-driven legal pages" },
  { id: "access-customer",  tab: "legal",   icon: ShieldCheck,  label: "Customer sign-in access",description: "Enable/disable customer login" },
  { id: "danger-zone",      tab: "legal",   icon: ShieldCheck,  label: "Danger zone",            description: "Reset demo data · destructive" },
];

const TABS = [
  { id: "brand",   label: "Brand",         icon: Building2 },
  { id: "theme",   label: "Theme",         icon: Palette },
  { id: "content", label: "Content",       icon: FileText },
  { id: "seo",     label: "SEO",           icon: Search },
  { id: "legal",   label: "Legal & Access",icon: ShieldCheck },
];

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function CMSEditor() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data } = useQuery({ queryKey: ["cms-landing-full"], queryFn: () => api.get("/cms/landing/full").then((r) => r.data) });

  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  useEffect(() => {
    if (data?.content) {
      setDraft(structuredClone(data.content));
      setOriginal(structuredClone(data.content));
    }
  }, [data]);

  const [activeTab, setActiveTab] = useState("brand");
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const scrollRef = useRef(null);

  // ─── Deep-link support: ?section=<id> ──────────────────────────────
  // On mount / URL change, honour the ?section=<id> param — auto-select
  // the correct tab, ensure the section is expanded, and scroll into view.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkSection = searchParams.get("section");
  useEffect(() => {
    if (!deepLinkSection || !draft) return;
    const target = SECTIONS.find((s) => s.id === deepLinkSection);
    if (!target) return;
    setActiveTab(target.tab);
    setActiveSection(target.id);
    setCollapsed((c) => {
      if (!c[target.id]) return c;
      const n = { ...c };
      delete n[target.id];
      return n;
    });
    // Wait for the tab-content DOM to render before scrolling.
    const id = requestAnimationFrame(() => {
      const el = document.getElementById(`cms-sec-${target.id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [deepLinkSection, draft?.hero]);

  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cms-collapsed") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("cms-collapsed", JSON.stringify(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);
  const toggleCollapsed = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const collapseAll = (tabId) => {
    const next = { ...collapsed };
    SECTIONS.filter((s) => s.tab === tabId).forEach((s) => { next[s.id] = true; });
    setCollapsed(next);
  };
  const expandAll = (tabId) => {
    const next = { ...collapsed };
    SECTIONS.filter((s) => s.tab === tabId).forEach((s) => { delete next[s.id]; });
    setCollapsed(next);
  };

  const save = useMutation({
    mutationFn: () => api.patch("/cms/landing", { content: draft }),
    onSuccess: () => {
      toast.success("Landing page updated live");
      setOriginal(structuredClone(draft));
      qc.invalidateQueries({ queryKey: ["cms-landing-full"] });
      qc.invalidateQueries({ queryKey: ["cms-landing"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const reset = useMutation({
    mutationFn: () => api.post("/cms/landing/reset"),
    onSuccess: (r) => {
      toast.success("Reset to defaults");
      setDraft(structuredClone(r.data.content));
      setOriginal(structuredClone(r.data.content));
      qc.invalidateQueries({ queryKey: ["cms-landing-full"] });
      qc.invalidateQueries({ queryKey: ["cms-landing"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Reset failed"),
  });

  const isDirty = useMemo(() => draft && original && !deepEqual(draft, original), [draft, original]);
  const dirtyCount = useMemo(() => {
    if (!isDirty) return 0;
    let n = 0;
    for (const k of Object.keys(draft || {})) {
      if (!deepEqual(draft[k], original?.[k])) n++;
    }
    return n;
  }, [draft, original, isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "You have unsaved CMS changes. Leave anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  if (!draft) return <div className="p-6 text-sm text-muted-foreground">Loading CMS…</div>;

  const set = (path, val) => {
    const next = structuredClone(draft);
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (cur[keys[i]] == null) cur[keys[i]] = {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = val;
    setDraft(next);
  };
  const setArr = (path, val) => set(path, val);

  const discard = () => {
    setDraft(structuredClone(original));
    toast.info("Discarded unsaved changes");
  };

  const sectionsForTab = SECTIONS.filter((s) => s.tab === activeTab);
  const filteredSections = search
    ? sectionsForTab.filter((s) => (`${s.label} ${s.description}`).toLowerCase().includes(search.toLowerCase()))
    : sectionsForTab;

  const jumpTo = (id) => {
    setActiveSection(id);
    if (collapsed[id]) setCollapsed((c) => { const n = { ...c }; delete n[id]; return n; });
    // Sync the URL so the section is deep-linkable / shareable.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("section", id);
      return next;
    }, { replace: true });
    requestAnimationFrame(() => {
      const el = document.getElementById(`cms-sec-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const copyDeepLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}?section=${id}`;
    const done = () => toast.success("Link copied — share it with your team");
    const failed = () => toast.error("Copy failed — link: " + url);
    // Prefer async Clipboard API; fall back to a hidden textarea for
    // environments where the API is unavailable or blocked (older browsers,
    // some iframes, non-secure origins).
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done, () => legacyCopy(url) ? done() : failed());
    } else {
      legacyCopy(url) ? done() : failed();
    }
  };

  const legacyCopy = (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  const lastEditedBy = data?.updated_by || "—";
  const lastEditedAt = data?.updated_at ? new Date(data.updated_at).toLocaleString() : "—";

  return (
    <div className="flex flex-col gap-4" data-testid="cms-editor-page">
      <header className="sticky top-0 z-20 -mx-4 border-b border-border/60 bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Palette size={20} className="text-primary" />
              <h1 className="font-display text-2xl font-black tracking-tight sm:text-3xl">Site content · CMS</h1>
              {isDirty && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest text-amber-900 dark:bg-amber-500/20 dark:text-amber-200"
                  data-testid="cms-dirty-badge"
                >
                  <Circle size={7} className="fill-amber-500 text-amber-500" />
                  {dirtyCount} unsaved {dirtyCount === 1 ? "section" : "sections"}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Changes go live instantly on save · Last edited by <span className="font-semibold">{lastEditedBy}</span> · {lastEditedAt}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm" data-testid="cms-open-preview"><Eye size={14} className="mr-1" /> Preview site</Button>
            </Link>
            <Link to="/portal/dashboard" target="_blank">
              <Button variant="outline" size="sm" data-testid="cms-open-customer-portal"><Users size={14} className="mr-1" /> Customer portal</Button>
            </Link>
            <Link to="/app/dashboard" target="_blank">
              <Button variant="outline" size="sm" data-testid="cms-open-admin-panel"><LayoutDashboard size={14} className="mr-1" /> Admin panel</Button>
            </Link>
            {isDirty && (
              <Button variant="ghost" size="sm" onClick={discard} data-testid="cms-discard-btn">
                <Undo2 size={14} className="mr-1" /> Discard
              </Button>
            )}
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="cms-reset-btn"><RotateCcw size={14} className="mr-1" /> Reset defaults</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset landing page?</AlertDialogTitle>
                    <AlertDialogDescription>All current CMS content will be replaced with factory defaults. This cannot be undone (but you can re-edit afterwards).</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => reset.mutate()}>Reset</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={save.isPending || !isDirty}
              data-testid="cms-save-button"
              className="min-w-[8rem]"
            >
              <Save size={14} className="mr-1" /> {save.isPending ? "Saving…" : isDirty ? "Save changes" : "All saved"}
            </Button>
          </div>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 gap-1" data-testid="cms-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-cms-${t.id}`} className="flex items-center gap-1.5">
                <Icon size={14} /> <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
              <aside className="lg:sticky lg:top-[92px] lg:h-[calc(100vh-120px)] lg:overflow-y-auto">
                <div className="relative mb-3">
                  <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search sections…"
                    className="h-9 pl-8 text-xs"
                    data-testid="cms-sidebar-search"
                  />
                </div>
                <nav className="space-y-0.5" data-testid={`cms-sidebar-${t.id}`}>
                  {filteredSections.length === 0 && (
                    <div className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                      No matches
                    </div>
                  )}
                  {filteredSections.map((s) => {
                    const Icon = s.icon;
                    const isActive = activeSection === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => jumpTo(s.id)}
                        className={`group flex w-full items-start gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors ${
                          isActive
                            ? "border-primary bg-primary/10"
                            : "border-transparent hover:border-border hover:bg-muted/60"
                        }`}
                        data-testid={`cms-nav-${s.id}`}
                      >
                        <Icon size={14} className={`mt-0.5 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                        <div className="min-w-0 flex-1">
                          <div className={`text-xs font-semibold ${isActive ? "text-primary" : "text-foreground"}`}>{s.label}</div>
                          <div className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">{s.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              <div ref={scrollRef} className="min-w-0 space-y-6">
                <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {sectionsForTab.length} section{sectionsForTab.length !== 1 ? "s" : ""} in this tab
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => collapseAll(t.id)} data-testid={`cms-collapse-all-${t.id}`}>
                      <ChevronsDownUp size={14} className="mr-1" /> Collapse all
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => expandAll(t.id)} data-testid={`cms-expand-all-${t.id}`}>
                      <ChevronsUpDown size={14} className="mr-1" /> Expand all
                    </Button>
                  </div>
                </div>
                {sectionsForTab.map((s) => (
                  <SectionCard
                    key={s.id}
                    section={s}
                    collapsed={!!collapsed[s.id]}
                    onToggle={() => toggleCollapsed(s.id)}
                    onCopyLink={() => copyDeepLink(s.id)}
                  >
                    <SectionBody id={s.id} draft={draft} set={set} setArr={setArr} />
                  </SectionCard>
                ))}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function SectionCard({ section, collapsed, onToggle, onCopyLink, children }) {
  const Icon = section.icon;
  return (
    <section
      id={`cms-sec-${section.id}`}
      className="scroll-mt-24 rounded-xl border border-border/60 bg-card shadow-sm"
      data-testid={`cms-section-${section.id}`}
    >
      <div className="flex w-full items-center gap-3 border-b border-border/60 px-5 py-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left transition-colors hover:opacity-80"
          aria-expanded={!collapsed}
          aria-controls={`cms-sec-body-${section.id}`}
          data-testid={`cms-toggle-${section.id}`}
        >
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
            <Icon size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-bold leading-tight tracking-tight">{section.label}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{section.description}</div>
          </div>
        </button>
        <button
          type="button"
          onClick={onCopyLink}
          title="Copy shareable link to this section"
          aria-label="Copy shareable link to this section"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
          data-testid={`cms-copy-link-${section.id}`}
        >
          <Link2 size={14} />
        </button>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${collapsed ? "-rotate-90" : "rotate-0"}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {!collapsed && (
        <div id={`cms-sec-body-${section.id}`} className="space-y-4 p-5">{children}</div>
      )}
    </section>
  );
}
